import { todayWithSystemTimeZone } from '@magicdoorinc/date';
import { ValidationError } from '~/errors';
import { validateId } from '~/repositories/utils';
import { urlWithQuery } from '~/utils/url';
import { BaseRestRepository } from './baseRestRepository';
import type { CreateOrUpdatePortfolioDto, HydratedPortfolioDto, PortfolioDto, PortfolioOverviewDto } from '~/swagger/portal/Api';
import type { ApiFilter } from '~/types/apiFilters';

const PORTFOLIO_URL = '/api/portfolios';

export function validatePortfolio(portfolio: HydratedPortfolioDto | CreateOrUpdatePortfolioDto): void {
  if (!portfolio) throw new ValidationError('Portfolio object must be provided');
}

export type PortfolioFilter = ApiFilter;

export class PortfolioRepository extends BaseRestRepository {
  public async getPortfolios(filter?: PortfolioFilter): Promise<HydratedPortfolioDto[]> {
    const url = urlWithQuery(PORTFOLIO_URL, filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getPortfolio(portfolioId: string): Promise<HydratedPortfolioDto> {
    validateId(portfolioId);
    const url = `${PORTFOLIO_URL}/${portfolioId}`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async batchGetPortfolioByIds(portfolioIds: string[]): Promise<HydratedPortfolioDto[]> {
    const url = urlWithQuery(`${PORTFOLIO_URL}/batch`, { portfolioIds });
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async createPortfolio(portfolio: CreateOrUpdatePortfolioDto): Promise<PortfolioDto> {
    validatePortfolio(portfolio);
    const url = PORTFOLIO_URL;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(portfolio),
    });
    return this.getJsonResponse(response);
  }

  public async updatePortfolio(portfolioId: string, portfolio: CreateOrUpdatePortfolioDto): Promise<PortfolioDto> {
    validateId(portfolioId);
    validatePortfolio(portfolio);
    const url = `${PORTFOLIO_URL}/${portfolioId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(portfolio),
    });
    return this.getJsonResponse(response);
  }

  /** Not preferred. use deactivatePortfolio instead in most cases */
  public async deletePortfolio(portfolioId: string): Promise<void> {
    validateId(portfolioId);
    const url = `${PORTFOLIO_URL}/${portfolioId}`;
    await this.fetchWithAuth(url, { method: 'DELETE' });
  }

  public async deactivatePortfolio(portfolioId: string, forceDeactivateChildren?: boolean): Promise<void> {
    validateId(portfolioId);
    const url = urlWithQuery(`${PORTFOLIO_URL}/${portfolioId}/deactivate`, { forceDeactivateChildren });
    await this.fetchWithAuth(url, { method: 'DELETE' });
  }

  public async getPortfolioOverview(portfolioId: string, useUpdatedAt?: boolean): Promise<PortfolioOverviewDto> {
    let url = `${PORTFOLIO_URL}/${portfolioId}/overview`;
    if (useUpdatedAt) {
      url += `?updatedAt=` + todayWithSystemTimeZone().valueOf();
    }
    const response = await this.fetchWithAuth(url, { method: 'GET' });
    return this.getJsonResponse(response);
  }
}

export const portfolioRepository = new PortfolioRepository();
